import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TuiTime } from '@taiga-ui/cdk';
import {
  TuiFilterByInputPipe,
  type TuiFilterByInputOptions,
  tuiItemsHandlersProvider,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  tuiCreateTimePeriods,
  TuiDataListWrapper,
  TuiInputTime,
} from '@taiga-ui/kit';

/**
 * Поле выбора времени. Значение контрола — строка `HH:mm` (или null).
 * Время можно ввести с клавиатуры или выбрать из списка с шагом `minutesGap`.
 */
@Component({
  selector: 'x-time-picker',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
    tuiItemsHandlersProvider({
      stringify: signal((time: TuiTime) => time.toString('HH:MM')),
      identityMatcher: signal(
        (a: TuiTime | null, b: TuiTime | null) => a?.valueOf() === b?.valueOf(),
      ),
    }),
  ],
  imports: [
    FormsModule,
    TuiTextfield,
    TuiInputTime,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
  ],
  templateUrl: './time-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimePickerComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  private onChange?: (value: string | null) => void;
  protected onTouched?: () => void;

  protected time: TuiTime | null = null;
  protected isDisabled = false;

  /**
   * Шаг минут в списке выбора. По умолчанию = 1.
   */
  $minutesGap = input<number>(1, { alias: 'minutesGap' });

  /**
   * Поле только для чтения: без ввода и без списка выбора.
   */
  $readOnly = input<boolean>(false, { alias: 'readOnly' });

  protected readonly $items = computed(() => {
    const gap = Math.max(1, Math.floor(this.$minutesGap()));
    const minutes = Array.from(
      { length: Math.ceil(60 / gap) },
      (_, index) => index * gap,
    ).filter(minute => minute < 60);

    return tuiCreateTimePeriods(0, 24, minutes);
  });

  // Фильтруем список только при частичном вводе: когда в поле уже стоит полное время
  // (например, выбранное ранее), показываем весь список, чтобы можно было выбрать другое
  protected readonly filter: TuiFilterByInputOptions<TuiTime>['filter'] = (
    items,
    query,
  ) =>
    query.length >= 5
      ? items
      : items.filter(time => time.toString('HH:MM').startsWith(query));

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: string | null | undefined): void {
    this.time = this.parseTime(value);
    this.cdr.markForCheck();
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  protected onTimeChange(time: TuiTime | null): void {
    this.time = time;
    this.onChange?.(time ? time.toString('HH:MM') : null);
  }

  private parseTime(value: string | null | undefined): TuiTime | null {
    const match = /^(\d{1,2}):(\d{2})/.exec(value ?? '');

    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    return hours < 24 && minutes < 60 ? new TuiTime(hours, minutes) : null;
  }
}
